import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, first, Observable, of, tap } from 'rxjs';
import { DATABASE_NAME } from 'src/app/config/app.config';
import { StorageService } from 'src/app/shared/services/storage/storage.service';
import { LOCAL_DATABASE } from '../../enums/storage/local-database.enum';
import { Logger } from '../../interfaces/Logger';
import { IBluetoothDevice } from '../../interfaces/bluetooth-device.interface';
import { STORE_DATABASE } from '../../enums/storage/store-database.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class BluetoothStorageService {
  private readonly storageService = inject(StorageService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _currentPrinter$: BehaviorSubject<IBluetoothDevice | null> =
    new BehaviorSubject<IBluetoothDevice | null>(null);

  private get bluetoothInstance(): LocalForage {
    return this.storageService.getInstance(
      DATABASE_NAME,
      STORE_DATABASE.current
    );
  }

  constructor() {
    this.getCurrentBluetoothInstance();
    this.loadCurrentPrinterIntoCache();
  }

  getCurrentPrinter(): Observable<IBluetoothDevice | null> {
    const cachedPrinter = this.getCurrentPrinterFromCache()?.getValue();

    if (cachedPrinter) {
      return of(cachedPrinter);
    }

    return this.getCurrentPrinterFromIndexedDB().pipe(
      first(),
      catchError((error) => {
        this.logger.error(new Error(`Error fetching current printer`), error);

        return of(null);
      })
    );
  }

  private getCurrentPrinterFromIndexedDB(): Observable<IBluetoothDevice | null> {
    return this.storageService
      .getItem<IBluetoothDevice>(
        this.bluetoothInstance,
        LOCAL_DATABASE.currentPrinter
      )
      .pipe(
        catchError((error) => {
          this.logger.error(new Error(`Error fetching current printer`), error);

          return of(null);
        })
      );
  }

  getCurrentPrinterFromCache(): BehaviorSubject<IBluetoothDevice | null> {
    return this._currentPrinter$;
  }

  insertCurrentPrinterIntoIndexedDB(
    currentPrinter: IBluetoothDevice
  ): Observable<IBluetoothDevice | null> {
    return this.storageService
      .setItem(
        this.bluetoothInstance,
        LOCAL_DATABASE.currentPrinter,
        currentPrinter
      )
      .pipe(
        first(),
        tap(() => this.setCurrentPrinterIntoCache(currentPrinter)),
        catchError((error) => {
          this.logger.error(
            new Error(
              `Error inserting current printer ${currentPrinter}: ${
                error.message || error
              }`
            )
          );

          return of(null);
        })
      );
  }

  removeCurrentPrinter(): Observable<void> {
    return this.storageService.removeItem(
      this.bluetoothInstance,
      LOCAL_DATABASE.currentPrinter
    );
  }

  removeAll(): void {
    this.removeCurrentPrinter();
  }

  private setCurrentPrinterIntoCache(
    currentPrinter: IBluetoothDevice | null
  ): void {
    this._currentPrinter$.next(currentPrinter);
  }

  private loadCurrentPrinterIntoCache(): void {
    this.getCurrentPrinterFromIndexedDB()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((printer: IBluetoothDevice | null) => {
        this.setCurrentPrinterIntoCache(printer);
      });
  }

  private getCurrentBluetoothInstance(): void {
    this.storageService.getInstance(DATABASE_NAME, STORE_DATABASE.current);
  }
}
